import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { InvitationPreview, LoginResult } from "@hik/contracts";
import { ApiError, get, post } from "../../lib/api";
import { Button } from "../../ui/Button";
import { ErrorSummary, TextField, useFieldError } from "../../ui/Field";
import { Skeleton } from "../../ui/States";
import { AuthLayout } from "./AuthLayout";

/** Davet: e-postadaki bağlantı (/davet/:token) veya İK'nın verdiği kısa kod (/davet). */
export function InvitePage() {
  const { token } = useParams();
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const navigate = useNavigate();
  if (!token) {
    return (
      <AuthLayout title="Davet kodunuzu girin" lead="İK ekibinizin size verdiği 8 karakterlik kodu yazın. Büyük/küçük harf ve tire önemli değil.">
        <form
          className="form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            const c = code.replace(/[^a-z0-9]/gi, "");
            // Eksik kod sessizce yutulmaz: ne gerektiği alanın altında yazar.
            if (c.length < 8) return setCodeError(c.length === 0 ? "Davet kodunu yazın." : "Davet kodu 8 karakterdir; eksik yazmış olabilirsiniz.");
            setCodeError(null);
            navigate(`/davet/${c}`);
          }}
        >
          <TextField
            label="Davet kodu"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoComplete="one-time-code"
            autoCapitalize="characters"
            placeholder="ÖR. HK7Q-4M2P"
            error={codeError ?? undefined}
            required
          />
          <Button type="submit" variant="primary" size="lg" className="btn--block">
            Devam
          </Button>
        </form>
      </AuthLayout>
    );
  }
  return <AcceptInvitation token={token} />;
}

function AcceptInvitation({ token }: { token: string }) {
  const preview = useQuery({ queryKey: ["invitation", token], queryFn: () => get<InvitationPreview>(`/auth/invitations/${token}`), retry: false });
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const fe = useFieldError();

  if (preview.isPending) return <AuthLayout title="Davetiniz kontrol ediliyor">{<Skeleton lines={2} />}</AuthLayout>;
  if (preview.error) {
    return (
      <AuthLayout title="Davet kullanılamıyor" footer={<Link to="/giris">Giriş ekranına dön</Link>}>
        <p className="notice notice--attention">{(preview.error as ApiError).message}</p>
      </AuthLayout>
    );
  }
  const inv = preview.data;
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await post<{ session: LoginResult["session"] }>(`/auth/invitations/${token}/accept`, { password, ...(fullName ? { fullName } : {}), client: "web" });
      qc.setQueryData(["session"], r.session);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }
  return (
    <AuthLayout
      title={`${inv.companyName} sizi bekliyor`}
      lead={
        inv.existingAccount
          ? "Bu bilgilerle zaten bir hesabınız var. Şirkete katılmak için mevcut parolanızı girin."
          : "Hesabınızı oluşturmak için bir parola belirleyin. Sonra izin, bordro ve belgelerinize buradan ulaşırsınız."
      }
    >
      <form onSubmit={submit} noValidate className="form">
        <ErrorSummary message={error?.message} fields={error?.fields} />
        <p className="form__identity">
          Giriş bilginiz: <strong>{inv.email ?? (inv.phone ? displayPhone(inv.phone) : "")}</strong>
        </p>
        {!inv.existingAccount && <TextField label="Adınız soyadınız" value={fullName || inv.fullName || ""} onChange={(e) => setFullName(e.target.value)} autoComplete="name" />}
        <TextField
          label={inv.existingAccount ? "Mevcut parolanız" : "Yeni parola"}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete={inv.existingAccount ? "current-password" : "new-password"}
          hint={inv.existingAccount ? undefined : "En az 10 karakter."}
          error={fe(error?.fields.password)}
          required
        />
        <Button type="submit" variant="primary" size="lg" busy={busy} className="btn--block">
          {inv.existingAccount ? "Şirkete katıl" : "Hesabımı oluştur"}
        </Button>
      </form>
    </AuthLayout>
  );
}

/** +905321234567 → 0532 123 45 67 (Türkiye cep numarası; diğerleri olduğu gibi). */
function displayPhone(e164: string): string {
  const m = /^\+90(\d{3})(\d{3})(\d{2})(\d{2})$/.exec(e164);
  return m ? `0${m[1]} ${m[2]} ${m[3]} ${m[4]}` : e164;
}
